/**
 * Coordinate system.
 *
 * Scene space is a local east-north-up tangent plane at the airport reference point,
 * in metres, expressed in three.js's right-handed Y-up frame:
 *   x = east, y = up (above the reference point's ellipsoid height), z = south (-north).
 *
 * Projection is equirectangular around the origin: dx = R cos(lat0) dlon, dz = -R dlat.
 * Within the ~30 km the tower can see, the difference from a true ENU frame is below
 * 0.1 % and the same function is used for every data source, so everything lines up.
 * Float32 resolves 30,000 m to about 2 mm, so no floating origin or double precision
 * emulation is needed. Earth curvature is not part of the projection; terrain vertices
 * get a curvature drop applied explicitly (see terrain.ts) because at 20 km it is 31 m
 * and visibly affects the horizon, while aircraft at that range are pixels.
 */
/** Minimal vector shape so this module has no renderer dependency; a three.js vector satisfies it. */
export interface XYZ { x: number; y: number; z: number }

export const EARTH_RADIUS_M = 6371008.8;
export const FT_TO_M = 0.3048;
export const KT_TO_MPS = 0.514444;
export const NM_TO_M = 1852;
export const DEG = Math.PI / 180;

export interface GeoOrigin {
  lat: number;
  lon: number;
  /** Ellipsoid/MSL height of the origin in metres; scene y = 0 here. */
  elevationM: number;
  cosLat: number;
}

export function createOrigin(lat: number, lon: number, elevationM: number): GeoOrigin {
  return { lat, lon, elevationM, cosLat: Math.cos(lat * DEG) };
}

/** lat/lon/MSL-metres to scene coordinates. */
export function toScene<T extends XYZ = XYZ>(o: GeoOrigin, lat: number, lon: number, altitudeM: number, out: T = { x: 0, y: 0, z: 0 } as T): T {
  out.x = (lon - o.lon) * DEG * EARTH_RADIUS_M * o.cosLat;
  out.z = -(lat - o.lat) * DEG * EARTH_RADIUS_M;
  out.y = altitudeM - o.elevationM;
  return out;
}

export function toGeo(o: GeoOrigin, p: XYZ): { lat: number; lon: number; altitudeM: number } {
  return {
    lon: o.lon + p.x / (EARTH_RADIUS_M * o.cosLat) / DEG,
    lat: o.lat - p.z / EARTH_RADIUS_M / DEG,
    altitudeM: p.y + o.elevationM,
  };
}

/** Curvature drop of a point at horizontal distance d from the eye: d² / 2R. */
export function curvatureDrop(dMeters: number): number {
  return (dMeters * dMeters) / (2 * EARTH_RADIUS_M);
}

/** Heading in degrees (0 = north, 90 = east) to a unit direction in scene space. */
export function headingToDir<T extends XYZ = XYZ>(headingDeg: number, out: T = { x: 0, y: 0, z: 0 } as T): T {
  const h = headingDeg * DEG;
  out.x = Math.sin(h); out.y = 0; out.z = -Math.cos(h);
  return out;
}

/** Scene-space direction to heading in degrees. */
export function dirToHeading(x: number, z: number): number {
  const h = Math.atan2(x, -z) / DEG;
  return (h + 360) % 360;
}

export function wrap180(deg: number): number {
  return ((((deg + 180) % 360) + 360) % 360) - 180;
}

/** Great-circle initial bearing and distance between two lat/lon pairs. */
export function bearingDeg(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const φ1 = lat1 * DEG, φ2 = lat2 * DEG, Δλ = (lon2 - lon1) * DEG;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (Math.atan2(y, x) / DEG + 360) % 360;
}

export function distanceM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const φ1 = lat1 * DEG, φ2 = lat2 * DEG, Δφ = (lat2 - lat1) * DEG, Δλ = (lon2 - lon1) * DEG;
  const a = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(a));
}

/** Destination point given start, bearing and distance (spherical). */
export function destination(lat: number, lon: number, bearing: number, dM: number): { lat: number; lon: number } {
  const δ = dM / EARTH_RADIUS_M, θ = bearing * DEG, φ1 = lat * DEG, λ1 = lon * DEG;
  const φ2 = Math.asin(Math.sin(φ1) * Math.cos(δ) + Math.cos(φ1) * Math.sin(δ) * Math.cos(θ));
  const λ2 = λ1 + Math.atan2(Math.sin(θ) * Math.sin(δ) * Math.cos(φ1), Math.cos(δ) - Math.sin(φ1) * Math.sin(φ2));
  return { lat: φ2 / DEG, lon: λ2 / DEG };
}

// --- Slippy map tile math (Web Mercator), used by the terrain/imagery loader. ---

export function lonToTileX(lon: number, z: number): number {
  return ((lon + 180) / 360) * 2 ** z;
}
export function latToTileY(lat: number, z: number): number {
  const φ = lat * DEG;
  return ((1 - Math.log(Math.tan(φ) + 1 / Math.cos(φ)) / Math.PI) / 2) * 2 ** z;
}
export function tileXToLon(x: number, z: number): number {
  return (x / 2 ** z) * 360 - 180;
}
export function tileYToLat(y: number, z: number): number {
  const n = Math.PI - (2 * Math.PI * y) / 2 ** z;
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
}

/** Cross-track distance (metres, signed: positive = right of track) of point P from the line through A on `bearing`. */
export function crossTrackM(aLat: number, aLon: number, bearing: number, pLat: number, pLon: number): number {
  const d = distanceM(aLat, aLon, pLat, pLon) / EARTH_RADIUS_M;
  const b = bearingDeg(aLat, aLon, pLat, pLon) * DEG;
  return Math.asin(Math.sin(d) * Math.sin(b - bearing * DEG)) * EARTH_RADIUS_M;
}

/** Along-track distance (metres, signed: positive = ahead along `bearing`) of point P from A. */
export function alongTrackM(aLat: number, aLon: number, bearing: number, pLat: number, pLon: number): number {
  const d = distanceM(aLat, aLon, pLat, pLon);
  const b = bearingDeg(aLat, aLon, pLat, pLon);
  return d * Math.cos((b - bearing) * DEG);
}
